import { useEffect, useState } from 'react';
import { getCustomSettings, saveCustomSetting } from 'src/Common/api/companySettingsApi';

// =====================================================================================
// Appointments + Commission module switches (company custom settings, category POS).
// Off unless the shop turns them on in /settings → POS Settings → Modules. Read from the
// session copy of the company settings (sessionStorage.companySettings), so the menu,
// the Setup hub and /reports can decide synchronously. The server enforces the same
// switches on every appointment / commission endpoint (403 APPOINTMENTS_DISABLED /
// COMMISSIONS_DISABLED), so this copy only decides what is shown.
// =====================================================================================

export const APPOINTMENT_SETTING_KEYS = {
  appointments: 'EnableAppointments',
  commissions: 'EnableCommissions',
  reminderMinutes: 'AppointmentReminderMinutes',
  slotMinutes: 'AppointmentSlotMinutes',
} as const;

/** Every key the Modules card owns (the generic POS custom-settings list skips them). */
export const APPOINTMENT_SETTING_KEY_LIST: string[] = Object.values(APPOINTMENT_SETTING_KEYS);

/** Fired on this tab when the switches change (the save below, or the missing-copy fill-in). */
export const APPOINTMENT_FLAGS_EVENT = 'cloudpos-appointment-flags-changed';
const REFRESH_EVENTS = [APPOINTMENT_FLAGS_EVENT, 'settingsChanged'];

export interface AppointmentFlags {
  /** EnableAppointments — calendar, dashboard, clients, staff schedules, reminders. */
  appointments: boolean;
  /** EnableCommissions — commission for any business, with or without appointments. */
  commissions: boolean;
  /**
   * The commission module is shown. A separate module (user decision 2026-10-25): ONLY its own switch
   * turns it on — Appointments on does not.
   */
  commissionAvailable: boolean;
  /** Minutes before an appointment the reminder goes out; 0 = no reminders. */
  reminderMinutes: number;
  /** Calendar / time-slot step in minutes (15 when unset). */
  slotMinutes: number;
}

const DESCRIPTIONS: Record<keyof typeof APPOINTMENT_SETTING_KEYS, string> = {
  appointments: 'Appointments: calendar, dashboard, clients and staff schedules',
  commissions: 'Commission: staff commission policies, approvals, settlements and reports',
  reminderMinutes: 'Appointments: send the reminder this many minutes before (0 = off)',
  slotMinutes: 'Appointments: time-slot step in minutes',
};

const isOn = (v: unknown) => v != null && String(v).trim().toLowerCase() === 'true';

const toInt = (v: unknown, fallback: number, min: number, max: number) => {
  const n = Number(v);
  if (v == null || String(v).trim() === '' || !Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(n)));
};

/** The session copy's customSettings dict, or null when the copy has none at all. */
function sessionCustomSettings(): Record<string, unknown> | null {
  try {
    const raw = sessionStorage.getItem('companySettings');
    if (!raw) return null;
    const s = JSON.parse(raw) as Record<string, unknown>;
    const cs = (s.customSettings ?? s.CustomSettings) as Record<string, unknown> | undefined;
    return cs && typeof cs === 'object' ? cs : null;
  } catch {
    return null;
  }
}

/** Switches from a key → value lookup (the session dict, or the custom-settings rows). */
export function appointmentFlagsFromValues(get: (key: string) => unknown): AppointmentFlags {
  const appointments = isOn(get(APPOINTMENT_SETTING_KEYS.appointments));
  const commissions = isOn(get(APPOINTMENT_SETTING_KEYS.commissions));
  return {
    appointments,
    commissions,
    commissionAvailable: commissions,
    reminderMinutes: toInt(get(APPOINTMENT_SETTING_KEYS.reminderMinutes), 0, 0, 10080),
    slotMinutes: toInt(get(APPOINTMENT_SETTING_KEYS.slotMinutes), 15, 5, 120),
  };
}

/** The shop's switches as this session knows them (all off when unknown). */
export function getAppointmentFlags(): AppointmentFlags {
  const cs = sessionCustomSettings() ?? {};
  return appointmentFlagsFromValues((key) => cs[key] ?? cs[key.charAt(0).toLowerCase() + key.slice(1)]);
}

export const isAppointmentsEnabled = (): boolean => getAppointmentFlags().appointments;
export const isCommissionAvailable = (): boolean => getAppointmentFlags().commissionAvailable;

/** Store types whose staff serve clients (same list as the Setup hub's booking stores). */
const SERVICE_STORE_TYPES = new Set([
  'saloon', 'salon', 'beauty saloon', 'beauty salon',
  'aesthetic clinics', 'aesthetic clinic',
  'photography studios', 'photography studio',
  'hotels', 'hotel', 'guest house', 'farm house',
]);

/**
 * A service business (salon, clinic, studio, hotel…), from `localStorage.storeType` (set at login):
 * its staff "serve" a client rather than "sell", which is how the checkout labels the staff picker.
 */
export function isServiceBusiness(): boolean {
  try {
    return SERVICE_STORE_TYPES.has((localStorage.getItem('storeType') || '').trim().toLowerCase());
  } catch {
    return false;
  }
}

function patchSession(values: Record<string, string>) {
  try {
    const raw = sessionStorage.getItem('companySettings');
    const s = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    const cs = { ...((s.customSettings ?? s.CustomSettings ?? {}) as Record<string, unknown>) };
    Object.entries(values).forEach(([key, value]) => {
      cs[key] = value;
    });
    s.customSettings = cs;
    sessionStorage.setItem('companySettings', JSON.stringify(s));
  } catch {
    /* the next settings refresh corrects it */
  }
}

/**
 * Save switches. The session copy is updated at once (a custom-setting save does not rewrite it
 * by itself) and this tab's menus are told; other sessions get the server's live push.
 */
export async function saveAppointmentFlags(patch: Partial<Omit<AppointmentFlags, 'commissionAvailable'>>): Promise<void> {
  const fields = Object.keys(patch) as Array<keyof typeof APPOINTMENT_SETTING_KEYS>;
  const values: Record<string, string> = {};
  for (const field of fields) {
    const value = String(patch[field]);
    await saveCustomSetting({
      settingKey: APPOINTMENT_SETTING_KEYS[field],
      settingValue: value,
      description: DESCRIPTIONS[field],
      category: 'POS',
      dataType: field === 'reminderMinutes' || field === 'slotMinutes' ? 'number' : 'boolean',
    });
    values[APPOINTMENT_SETTING_KEYS[field]] = value;
  }
  patchSession(values);
  window.dispatchEvent(new Event(APPOINTMENT_FLAGS_EVENT));
}

let fillInStarted = false;

/**
 * A company without a CompanySettings row gets a session copy with no customSettings dict at all,
 * so every switch would read OFF even when it is on. In that case only, read the rows once and
 * fill the dict in. A dict that simply lacks the key means the switch is off — nothing is fetched.
 */
export async function ensureAppointmentFlagsLoaded(): Promise<void> {
  if (fillInStarted || sessionCustomSettings() !== null) return;
  if (!sessionStorage.getItem('companySettings') && !localStorage.getItem('authToken')) return;
  fillInStarted = true;
  try {
    const res: any = await getCustomSettings();
    const rows: any[] = res?.customSettings || res?.CustomSettings || [];
    const values: Record<string, string> = {};
    rows.forEach((r) => {
      const key = String(r?.settingKey ?? r?.SettingKey ?? '');
      if (APPOINTMENT_SETTING_KEY_LIST.includes(key)) values[key] = String(r?.settingValue ?? r?.SettingValue ?? '');
    });
    patchSession(values);
    if (Object.keys(values).length > 0) window.dispatchEvent(new Event(APPOINTMENT_FLAGS_EVENT));
  } catch {
    fillInStarted = false;
  }
}

const sameFlags = (a: AppointmentFlags, b: AppointmentFlags) =>
  a.appointments === b.appointments &&
  a.commissions === b.commissions &&
  a.reminderMinutes === b.reminderMinutes &&
  a.slotMinutes === b.slotMinutes;

/** The shop's switches, kept current while a screen is open (re-renders only on a real change). */
export function useAppointmentFlags(): AppointmentFlags {
  const [flags, setFlags] = useState<AppointmentFlags>(() => getAppointmentFlags());
  useEffect(() => {
    const refresh = () => {
      const next = getAppointmentFlags();
      setFlags((prev) => (sameFlags(prev, next) ? prev : next));
    };
    REFRESH_EVENTS.forEach((e) => window.addEventListener(e, refresh));
    refresh();
    void ensureAppointmentFlagsLoaded();
    return () => {
      REFRESH_EVENTS.forEach((e) => window.removeEventListener(e, refresh));
    };
  }, []);
  return flags;
}
